import { isBefore } from 'date-fns';

import { startOfMinute } from 'date-fns';

import { formatDistanceToNowStrict } from 'date-fns';
import { endOfMinute } from 'date-fns';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import { CLIENT_ORIGIN } from '~/lib/constants';
import type { OrganizationData } from '~/types/organization';
import type { SubscriptionPlan } from '~/types/subscription';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

const sendRequestWithAuth = requestWithAuth(CLIENT_ORIGIN);

interface Props {
  plan: SubscriptionPlan;
  org?: OrganizationData | null;
}

export const RefreshSubscriptionButton = ({ plan, org }: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  async function handleRefreshSubscription() {
    setIsLoading(true);

    const canCall = canCallRefreshEndpoint();

    if (!canCall) {
      const currentWindowEnd = endOfMinute(new Date());

      toast.error(
        `You can only refresh your subscription once per minute. Please wait ${formatDistanceToNowStrict(currentWindowEnd)} before trying again`,
      );
      setIsLoading(false);

      return;
    }

    const url =
      plan === 'pro'
        ? 'api/membership/refresh'
        : `api/organizations/${org?.provider}/${org?.slug}/subscription/refresh`;

    try {
      await withSpinner({
        action: async () => {
          await sendRequestWithAuth<{ sessionUrl: string }>(url, {}, 'POST', {
            429: async (response) => {
              const body = await response.json();

              if (typeof body === 'object' && body !== null && 'errors' in body && Array.isArray(body.errors)) {
                toast.error(body.errors[0] || 'Too many requests. Please try again later.');
              }

              return true;
            },
          });

          toast('Subscription data updated, the page will reload now');

          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        onStateUpdate: setIsLoading,
      });
    } catch (error) {
      logger.error(error);

      toast.error(error instanceof Error ? error.message : 'Error while refreshing subscription data');
    } finally {
      persistLastRefreshCallTimestamp();
    }
  }

  return (
    <div className="w-full flex items-center justify-end">
      <Button
        variant="tint"
        size="md"
        disabled={isLoading}
        onClick={handleRefreshSubscription}
        loading={isLoading}
        loader={<LoadingDots text="Refreshing" />}
        startIcon="i-ph:arrow-counter-clockwise"
      >
        Refresh subscription data
      </Button>
    </div>
  );
};

const REFRESH_CALL_STORAGE_KEY = 'last-called-refresh-endpoint-at';

function persistLastRefreshCallTimestamp() {
  localStorage.setItem(REFRESH_CALL_STORAGE_KEY, Date.now().toString());
}

function getLastRereshCallTimestamp() {
  const storedValue = localStorage.getItem(REFRESH_CALL_STORAGE_KEY);

  if (!storedValue) {
    return 0;
  }

  return Number.parseInt(storedValue);
}

function canCallRefreshEndpoint() {
  const lastCalledAt = getLastRereshCallTimestamp();

  if (!lastCalledAt) {
    return true;
  }

  const now = new Date();
  const lastCalledAtDate = new Date(lastCalledAt);

  const currentWindowStart = startOfMinute(now);

  return isBefore(lastCalledAtDate, currentWindowStart);
}
